import React from "react";
import PropTypes from "prop-types";
import "./PanelBase.css";
import { useHoverInfoStore } from "../../store/chart/hoverInfoStore";
import { useChartsStore } from "../../store/chart/chartStore";
import { useReferentialPriceStore } from "../../store/referentialPriceStore";
import {
  useStateSignalsStore,
  isAuctionState,
} from "../../store/stateSignalsStore";
import { isFeatureEnabled } from "../../data/Services/settings";
import marketdata from "../../data/Services/marketdata";

/**
 * VWAP da sessão do símbolo — campo VW do quote, já recebido e coalescido pelo
 * quoteWorker no cache de quotes (dado do SERVIDOR; nunca calculado aqui).
 *
 * Poll de 1s em vez de assinar o stream: o hover NÃO re-renderiza com o cursor
 * parado (guard de bar-change do hoverInfoStore), então ler o cache só no
 * build das linhas congelaria um dado vivo; e assinar o receiver de quotes
 * re-renderizaria o painel ~4×/s por nada. Um Map.get por segundo, com
 * setState apenas quando o valor muda, mantém I5 e o painel atualizado.
 */
const useSessionVwap = (symbol) => {
  const [vwap, setVwap] = React.useState(null);
  React.useEffect(() => {
    if (!symbol) {
      setVwap(null);
      return undefined;
    }
    const read = () => {
      const vw = marketdata.getLastQuote?.(symbol)?.VW;
      const next = typeof vw === "number" && isFinite(vw) && vw > 0 ? vw : null;
      setVwap((prev) => (prev === next ? prev : next));
    };
    read();
    const id = setInterval(read, 1000);
    return () => clearInterval(id);
  }, [symbol]);
  return vwap;
};
import { buildInfoRows } from "./buildInfoRows";
import indicatorRegistry from "../../Registry/Indicators/IndicatorRegistry";
import { useThemeStore } from "../../store/themeStore.jsx";
import { isDarkTheme } from "../../config/themes";
import { formatPrice } from "../../utils/priceUtils.js";
import { formatNumber } from "../../utils/numberFormatting.js";
import { getNumericFontFamily } from "../../utils/fontUtils.js";
import { PanelToolbar, PanelToolbarHost } from "./PanelToolbar";

// Formata a data/hora da barra conforme a periodicidade (intraday mostra hora).
const isIntraday = (periodicity) =>
  typeof periodicity === "string" && /min|h|sec|tick|s$/i.test(periodicity);

const pad2 = (n) => String(n).padStart(2, "0");

const formatBarDate = (value, periodicity) => {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "---";
  const dmy = `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${String(
    d.getFullYear(),
  ).slice(-2)}`;
  return isIntraday(periodicity)
    ? `${dmy} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
    : dmy;
};

/**
 * Painel "Info" — contraparte do painel Info do Silverlight. Mostra, para a barra
 * exata sob o cursor do gráfico onde o mouse está, os dados da barra + os valores
 * de todos os indicadores de todos os chart areas daquele gráfico.
 *
 * Não-grupo, read-only. Vale sempre o chart sob o mouse (sem "select"); congela
 * ao sair do chart (o store nunca limpa no mouseleave). Assina o useHoverInfoStore
 * isolado — re-renderiza só quando a barra sob o cursor muda.
 */
const InfoPanel = ({
  initialToolbarPinned = true,
  onToolbarPinnedChange,
  claimFocus,
}) => {
  const theme = useThemeStore((state) => state.theme);
  const isDarkMode = isDarkTheme(theme);

  const [toolbarPinned, setToolbarPinned] =
    React.useState(initialToolbarPinned);
  const handleToolbarPinnedChange = React.useCallback(
    (pinned) => {
      setToolbarPinned(pinned);
      onToolbarPinnedChange?.(pinned);
    },
    [onToolbarPinnedChange],
  );

  // Barra sob o cursor (store isolado). Re-render só no bar-change.
  const lastHoveredChartId = useHoverInfoStore((s) => s.lastHoveredChartId);
  const entry = useHoverInfoStore((s) =>
    lastHoveredChartId ? s.hovered[lastHoveredChartId] : null,
  );
  // Gráfico hovered — para enumerar indicadores + símbolo/periodicidade.
  const chart = useChartsStore((s) =>
    lastHoveredChartId
      ? s.charts.find((c) => c.id === lastHoveredChartId)
      : null,
  );

  const symbol = chart?.symbol;
  const periodicity = chart?.periodicity;

  // Ajuste (settlement D-1, #437) — dado CORRENTE do símbolo, chave EXATA (o
  // feed já re-emite RP keyed por alias FUT/FUL; resolução de família aqui
  // casaria contrato expirado no rollover). Flag em USE time = kill switch.
  const rpEntry = useReferentialPriceStore((s) =>
    symbol ? s.bySymbol[symbol] : null,
  );
  const ajuste =
    isFeatureEnabled("infoAjuste") && rpEntry && rpEntry.ps > 0
      ? rpEntry.ps
      : null;
  // Ajuste D0 (settlement apurado HOJE, campo SP do RP) — mesma flag do D-1.
  const ajusteD0 =
    isFeatureEnabled("infoAjuste") && rpEntry && rpEntry.sp > 0
      ? rpEntry.sp
      : null;

  // Fase/Teórico/Em Aberto (eventos "SS"/"TP"/"OI", marketdata#25).
  const phaseEntry = useStateSignalsStore((s) =>
    symbol ? s.phaseBySymbol[symbol] : null,
  );
  const theoEntry = useStateSignalsStore((s) =>
    symbol ? s.theoreticalBySymbol[symbol] : null,
  );
  const oiEntry = useStateSignalsStore((s) =>
    symbol ? s.openInterestBySymbol[symbol] : null,
  );
  const fase =
    isFeatureEnabled("infoFase") && phaseEntry?.state ? phaseEntry.state : null;
  // Teórico SÓ com fase em leilão (decisão 04/08): fora de leilão o valor é
  // stale por natureza — a B3 não emite "fim do teórico".
  const teorico =
    fase && isAuctionState(fase) && theoEntry && theoEntry.p > 0
      ? theoEntry.p
      : null;
  const emAberto =
    isFeatureEnabled("infoOpenInterest") && oiEntry && oiEntry.v > 0
      ? oiEntry.v
      : null;

  // VWAP da sessão — dado já disponível no cliente (campo VW do quote); sem
  // flag própria: a linha viaja sob o gate do próprio painel (infoPanel).
  const vwap = useSessionVwap(symbol);

  const fmt = React.useMemo(
    () => ({
      price: (v) => formatPrice(symbol, v),
      num: (v) => formatNumber(v, 2),
      int: (v) => formatNumber(v, 0),
      pct: (v) => `${formatNumber(v, 2)}%`,
      date: (v) => formatBarDate(v, periodicity),
    }),
    [symbol, periodicity],
  );

  const rows = React.useMemo(() => {
    if (!entry?.bar || !chart) return [];
    return buildInfoRows({
      bar: entry.bar,
      prevClose: entry.prevClose,
      dayPrevClose: entry.dayPrevClose,
      chart,
      symbol,
      registry: indicatorRegistry,
      ajuste,
      ajusteD0,
      fase,
      teorico,
      emAberto,
      vwap,
      // Diária+ (1D/1W/1M): "Var. dia" duplicaria "Variação" — omitido.
      isDaily: ["1D", "1W", "1M"].includes(periodicity),
      fmt,
    });
  }, [
    entry,
    chart,
    symbol,
    ajuste,
    ajusteD0,
    fase,
    teorico,
    emAberto,
    vwap,
    periodicity,
    fmt,
  ]);

  const toneColor = (tone) =>
    tone === "up" ? "#16a34a" : tone === "down" ? "#dc2626" : undefined;

  return (
    <PanelToolbarHost
      pinned={toolbarPinned}
      onMouseDown={claimFocus}
      className="vertical-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        backgroundColor: isDarkMode
          ? "var(--tz-surface-deep, #1f2937)"
          : undefined,
      }}
      contentStyle={{
        padding: "6px 8px",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        minHeight: 0,
        overflow: "auto",
      }}
      toolbar={
        <PanelToolbar
          pinned={toolbarPinned}
          onPinnedChange={handleToolbarPinnedChange}
          pinDataEvoId="infopanel-btn-toolbar-pin"
        >
          <h5>Info{symbol ? ` — ${symbol}` : ""}</h5>
        </PanelToolbar>
      }
    >
      {rows.length === 0 ? (
        <div
          style={{
            flex: "1 1 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9ca3af",
            fontSize: "12px",
            textAlign: "center",
            padding: "0 12px",
          }}
        >
          Passe o mouse sobre um gráfico para ver os dados da barra.
        </div>
      ) : (
        <div data-evo-id="infopanel-rows">
          {rows.map((r) =>
            r.isHeader ? (
              <div
                key={r.id}
                style={{
                  marginTop: "8px",
                  paddingBottom: "2px",
                  borderBottom: "1px solid var(--surface-border)",
                  color: "#9ca3af",
                  fontSize: "10px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                }}
              >
                {r.title}
              </div>
            ) : (
              <div
                key={r.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: "8px",
                  padding: "2px 0",
                  fontSize: "11px",
                }}
              >
                <span style={{ color: "#9ca3af", flex: "0 0 auto" }}>
                  {r.title}
                </span>
                <span
                  style={{
                    color: toneColor(r.tone) || "var(--text-color)",
                    fontWeight: 600,
                    fontFamily: getNumericFontFamily(),
                    textAlign: "right",
                    flex: "1 1 auto",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.value}
                </span>
              </div>
            ),
          )}
        </div>
      )}
    </PanelToolbarHost>
  );
};

InfoPanel.propTypes = {
  initialToolbarPinned: PropTypes.bool,
  onToolbarPinnedChange: PropTypes.func,
  claimFocus: PropTypes.func,
};

export default React.memo(InfoPanel);
